import { ReactNode, useMemo, useState } from 'react';
import { useMediaQuery } from '@providers/MediaQueryProvider';
import { useNavigate } from '@tanstack/react-router';

import { type ChessBoardProps } from '@exports/ChessBoard';
import { ChessGame, useArrows } from '@exports/ChessGame';
import { useBoardSounds } from '@exports/hooks/useBoardSounds';
import { useBoardTheme } from '@exports/hooks/useBoardTheme';
import { Scoresheet } from '@exports/Scoresheet';
import { downloadPgn } from '@libs/chess/downloadPgn';
import { parseFen } from '@libs/chess/parseFen';
import { SquareName } from '@libs/chess/types';
import { cx } from '@libs/classnames';

import { useAutoPromotion } from '@features/game/hooks/useAutoPromotion';
import { GameConfig, useComputerGame } from '@features/game/hooks/useComputerGame';
import { useScoresheet } from '@features/game/hooks/useScoresheet';
import {
  ButtonDownloadPgn,
  ButtonPlayAgain,
  ButtonResign,
} from '@features/game/ui/GameButtons/GameButtons';
import { GameInfo } from '@features/game/ui/GameInfo/GameInfo';
import { GameLayout, GameLayoutSkeleton } from '@features/game/ui/GameLayout/GameLayout';
import { PlayerPanelWidget } from '@features/game/ui/PlayerPanel';
import { components } from '@features/shared/api/game';
import { IValidRoute } from '@features/shared/utils/router';

import { ComputerSettings } from './components/ComputerSettings/ComputerSettings';
import { useSettingsMQ } from './components/ComputerSettings/useSettingsMQ';
import { CATALOG_ELO, DEFAULT_SETTINGS } from './contrants';
import { IGameSettings, IGameSettingsTime } from './types';

import styles from './ComputerGamePage.module.css';

const TIME_PRESSURE = 10;

const BACK_LINK: IValidRoute = '/';

export type Props = {
  avatar?: ReactNode;
  fen?: string;
  elo?: number;
  color?: 'white' | 'black' | 'alternate';
  mode?: 'settings' | 'positions' | 'random' | 'play';
  time?: {
    initial: number;
    perMove: number;
  };
};

// Минимальный срез игры, для Scoresheet и PlayerPanel
type GameMinimal = {
  // scoresheet + player
  id: string;
  // scoresheet
  moves: MoveMinimal[];
  // scoresheet + player
  result: components['schemas']['GameResult'];
  // player
  status: components['schemas']['GameStatus'];
  white: PlayerMinimal;
  black: PlayerMinimal;
  nextMoveBy: 'Black' | 'White';
  clock?: {
    timestampMs: number;
    blackLeftMs?: number;
    whiteLeftMs?: number;
  };
};

type PlayerMinimal = {
  id: string;
  firstName: string;
  lastName: string;
  rating?: number;
};

type MoveMinimal = {
  san: string;
  from: string;
  to: string;
  fenAfter: string;
  clockAfter?: {
    whiteLeftMs: number;
    blackLeftMs: number;
  };
};

// TODO:
// - [ ] Проброс темы (но какой-нибудь аккуратный, без сохранения)
// - [ ] Убрать лишние анимации при рестарте игры (происходит из-за setState в useChessGame во время рендера)
// - [ ] Убрать selected state при завершении игры (чтобы "target" не зависал)
// - [ ] Починить анимацию появления настроек (блюр не совместим с анимациями)
// - [ ] Сделать скругления доски в настройках как на макете

export function ComputerGamePage({
  avatar,
  elo,
  color: initialColor,
  mode = 'settings',
  time: initialTime,
}: Props) {
  const isMobile = useMediaQuery() !== 'desktop';
  const { isMobile: areSettingsMobile } = useSettingsMQ();
  const { boardTheme } = useBoardTheme();
  const navigate = useNavigate();

  const [settings, setSettings] = useState<IGameSettings>(() => {
    const r = { ...DEFAULT_SETTINGS, id: `from-url-${Date.now()}` };
    if (initialColor) r.color = initialColor;
    if (elo !== undefined) r.elo = elo;
    if (initialTime) {
      r.white = r.black = {
        noTimeControl: false,
        timeInitialSec: initialTime.initial * 60,
        timeAddSec: initialTime.perMove,
      };
    }
    if (mode === 'positions') r.fenMode = 'catalog';
    return r;
  });
  const [settingsInited, setSettingsInited] = useState(mode !== 'settings' && mode !== 'positions');

  const [showSettings, setShowSettings] = useState(mode === 'settings' || mode === 'positions');
  const [gameInfo, setGameInfo] = useState(() => ({
    num: 0,
    prevColor: settings.color === 'alternate' ? 'black' : settings.color,
  }));
  const holdPlay = (gameInfo.num === 0 && showSettings) || !settingsInited;

  const { autoPromotion } = useAutoPromotion();
  const gameConfig = useMemo<GameConfig>(() => {
    const color = (() => {
      if (settings.fenMode === 'catalog') {
        return parseFen(settings.fenCatalog)!.color;
      }

      return settings.color === 'alternate'
        ? gameInfo.prevColor === 'white'
          ? 'black'
          : 'white'
        : settings.color;
    })();

    return {
      id: `${gameInfo.num}-${Date.now()}`,
      color: color,
      fen: settings.fenMode === 'manual' ? settings.fenManual : settings.fenCatalog,
      elo: settings.fenMode === 'catalog' ? CATALOG_ELO : settings.elo,
      black: settings.black.noTimeControl ? undefined : settings.black,
      white: settings.white.noTimeControl ? undefined : settings.white,
    };
  }, [settings, gameInfo]);

  const { boardSounds } = useBoardSounds();
  const engine = useComputerGame({
    cfg: gameConfig,
    disabled: holdPlay,
  });
  const { snapshot } = engine;

  const side = gameConfig.color;
  const lastMove = snapshot.lastMove ?? undefined;
  const premove = snapshot.premove ?? undefined;

  const game: GameMinimal = (() => {
    const you = { id: 'player', firstName: 'Вы', lastName: '' };
    const computer = {
      id: 'computer',
      firstName: 'Компьютер',
      lastName: '',
      rating: gameConfig.elo,
    };

    return {
      id: '',
      status: holdPlay ? 'NotStarted' : snapshot.winner ? 'Finished' : 'InProgress',
      result: holdPlay ? 'NotStarted' : snapshot.winner ? mapWinner(snapshot.winner) : 'InProgress',
      nextMoveBy: snapshot.turn === 'white' ? 'White' : 'Black',
      moves: snapshot.moves as MoveMinimal[],
      white: gameConfig.color === 'white' ? you : computer,
      black: gameConfig.color === 'black' ? you : computer,
      clock:
        snapshot.whiteClockMs || snapshot.blackClockMs
          ? {
              whiteLeftMs: snapshot.whiteClockMs,
              blackLeftMs: snapshot.blackClockMs,
              timestampMs: snapshot.timestampMs,
            }
          : undefined,
    };
  })();

  const scoresheet = useScoresheet({
    game,
    boardProps: { winner: snapshot.winner } as ChessBoardProps,
    autoResetOnMove: true,
  });
  const customPlayersTimeMs =
    !scoresheet.isLastMove || game?.status === 'Finished' ? scoresheet.playersTimeMs : undefined;

  const handleNewGame = () => {
    setSettingsInited(true);
    setGameInfo((n) => ({
      num: n.num + 1,
      prevColor: gameConfig.color,
    }));
  };

  const handleShowSettings = () => {
    setShowSettings(true);
  };
  const handleHideSettings = () => {
    if (settingsInited) setShowSettings(false);
  };
  const handleSettingsCancel = () => {
    if (!settingsInited) {
      navigate({ to: BACK_LINK });
    } else {
      setShowSettings(false);
    }
  };

  const handleSettingsApply = (nextSettings: IGameSettings) => {
    setSettingsInited(true);
    setSettings(nextSettings);
    setShowSettings(false);
    setGameInfo((n) => ({
      num: n.num + 1,
      prevColor: nextSettings.color === 'alternate' ? 'black' : nextSettings.color,
    }));
  };

  const handleDownloadPgn = () => {
    const playerColor = parseFen(gameConfig.fen)?.color;
    const opponentColor = playerColor === 'black' ? 'white' : 'black';

    downloadPgn({
      filename: `computer-${Date.now()}.pgn`,
      site: 'https://play.chessopen.ru',
      winner: snapshot.winner ?? null,
      link: 'https://play.chessopen.ru/game/computer',
      fen: gameConfig.fen,
      white: playerColor === 'white' ? 'Player' : 'Computer',
      black: playerColor === 'black' ? 'Player' : 'Computer',
      date: new Date().getTime(),
      moves: snapshot.moves.map((m, i) => {
        const side = i % 2 === 0 ? playerColor : opponentColor;
        return {
          san: m.san,
          timeRemaining: m.clockAfter
            ? Math.floor(m.clockAfter[side === 'white' ? 'whiteLeftMs' : 'blackLeftMs'] / 1000)
            : undefined,
        };
      }),
      mode: 'ICS',
    });
  };

  const patch = scoresheet.propsPatch;
  const silent = !!scoresheet.props.selected;

  const displayFen = patch ? patch.position : snapshot.fen;
  const { arrows, onUserArrow } = useArrows(displayFen);

  if (snapshot.state === 'initializing') {
    return <GameLayoutSkeleton />;
  }

  return (
    <>
      <GameLayout
        backLink={BACK_LINK}
        backLinkIsLogo
        board={
          <ChessGame
            key={game.id}
            className={holdPlay ? styles.is_disabled : undefined}
            fen={displayFen}
            side={patch ? 'none' : side}
            lastMove={
              patch
                ? {
                    from: patch.selectedFrom as SquareName,
                    to: patch.selectedTo as SquareName,
                  }
                : lastMove
            }
            premove={patch ? undefined : premove}
            allowPremove
            autoPromotion={autoPromotion}
            boardSounds={boardSounds}
            silent={silent}
            winner={patch ? undefined : snapshot.winner}
            onMove={engine.store.onPlayerMove}
            onPremove={engine.store.onPlayerPremove}
            orientation={gameConfig.color}
            theme={boardTheme}
            notation={isMobile ? 'inside' : undefined}
            arrows={arrows}
            onUserArrow={onUserArrow}
          />
        }
        avatar={avatar}
        header={
          <GameInfo
            title="Игра с компьютером"
            mobileBackLink={BACK_LINK}
            mobileBacklinkIsLogo
            twoAvatars
            specs={formatGameSpecs(settings, gameConfig.color)}
          />
        }
        player1={
          <PlayerPanelWidget
            orientation="top"
            color={gameConfig.color === 'black' ? 'white' : 'black'}
            game={game}
            mobile={isMobile}
            timePressureSec={TIME_PRESSURE}
            scoresheetTimeMs={customPlayersTimeMs}
          />
        }
        player2={
          <PlayerPanelWidget
            orientation="bottom"
            color={gameConfig.color === 'white' ? 'white' : 'black'}
            game={game}
            mobile={isMobile}
            timePressureSec={TIME_PRESSURE}
            scoresheetTimeMs={customPlayersTimeMs}
          />
        }
        scoresheet={<Scoresheet {...scoresheet.props} enableGlobalShortcuts />}
        actions={
          <div className={styles.actions}>
            {game.status === 'InProgress' && (
              <ButtonResign onClick={() => engine.store.onPlayerSurrender()} />
            )}
            {game.status === 'Finished' && (
              <>
                <ButtonDownloadPgn onClick={handleDownloadPgn}>Скачать PGN</ButtonDownloadPgn>
                <ButtonPlayAgain onClick={handleNewGame} onSettings={handleShowSettings} />
              </>
            )}
          </div>
        }
      />

      {showSettings && (
        <ComputerSettings
          className={cx(styles.settings, {
            [styles.is_mobile]: areSettingsMobile,
          })}
          initialSettings={settings}
          isMobile={areSettingsMobile}
          onPlay={handleSettingsApply}
          onClose={handleHideSettings}
          onCancel={handleSettingsCancel}
        />
      )}
    </>
  );
}

function mapWinner(winner: 'draw' | 'black' | 'white'): components['schemas']['GameResult'] {
  switch (winner) {
    case 'black':
      return 'BlackWin';
    case 'draw':
      return 'Draw';
    case 'white':
      return 'WhiteWin';
  }
}

function formatGameSpecs(cfg: IGameSettings, color: 'black' | 'white'): string[] {
  const top = color === 'white' ? cfg.black : cfg.white;
  const bottom = cfg[color];

  const topSpec = formatPlayerSpecs(top);
  const bottomSpec = formatPlayerSpecs(bottom);

  if (topSpec[0] === bottomSpec[0]) return topSpec;

  return topSpec[0] === bottomSpec[0] ? topSpec : [...topSpec, ...bottomSpec];
}

function formatPlayerSpecs(time: IGameSettingsTime): string[] {
  if (time.noTimeControl) {
    return ['Без контроля времени'];
  }

  const minutes = Math.floor(time.timeInitialSec / 60);

  const format = (() => {
    if (minutes < 10) return 'Блиц';
    if (minutes < 60) return 'Рапид';
    return 'Классические';
  })();

  return [`${minutes}+${time.timeAddSec ?? 0}`, format];
}
